import { useEffect, useRef, useState } from "react";
import { ICurrency, IExpense, IExpenseUpdate } from "../models/IExpense";
import { IExpenseVendor } from "../models/IExpenseVendor";
import {
  addExpenseDocuments,
  deleteExpense,
  deleteExpenseDocument,
  getPlatformCurrencyList,
  patchExpenseReimbursed,
  updateExpense,
} from "../services/expense";
import VendorPicker from "./VendorPicker";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const MAX_FILE_BYTES = 2 * 1024 * 1024;

const formatDt = (iso: string) =>
  new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

const formatDateForInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface Props {
  expense: IExpense;
  onClose: () => void;
  onRefresh: () => void;
}

export default function ExpenseDetailModal({ expense, onClose, onRefresh }: Props) {
  const [editing, setEditing] = useState(false);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [currLoading, setCurrLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<IExpenseVendor | null>(
    expense.vendorId ? { id: expense.vendorId, name: expense.vendorName ?? "", note: "", isEnabled: true, createdAt: "" } : null
  );
  const [showVendorPicker, setShowVendorPicker] = useState(false);

  const [form, setForm] = useState<IExpenseUpdate>({
    spentByUserId: expense.spentByUserId,
    amount: expense.amount,
    currencyId: expense.currencyId,
    spentAt: expense.spentAt,
    description: expense.description,
  });
  const [spentAtLocal, setSpentAtLocal] = useState(formatDateForInput(expense.spentAt));

  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [isReimbursed, setIsReimbursed] = useState(expense.isReimbursed);
  const [reimbursing, setReimbursing] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [docFileError, setDocFileError] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);

  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 640);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    if (editing && currencies.length === 0) {
      setCurrLoading(true);
      getPlatformCurrencyList()
        .then((res) => setCurrencies(res.data))
        .catch(() => {})
        .finally(() => setCurrLoading(false));
    }
  }, [editing]);

  const handleSave = async () => {
    if (!form.description.trim()) { setEditError("Açıklama boş olamaz."); return; }
    if (form.amount <= 0) { setEditError("Geçerli bir tutar giriniz."); return; }
    setSaving(true);
    setEditError(null);
    try {
      await updateExpense(expense.id, {
        ...form,
        vendorId: selectedVendor?.id,
        clearVendor: !selectedVendor && !!expense.vendorId,
        spentAt: new Date(spentAtLocal).toISOString(),
      });
      setEditing(false);
      onRefresh();
    } catch {
      setEditError("Güncelleme başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleReimbursed = async () => {
    const next = !isReimbursed;
    setReimbursing(true);
    try {
      await patchExpenseReimbursed(expense.id, next);
      setIsReimbursed(next);
    } catch {
      // revert on error — state unchanged
    } finally {
      setReimbursing(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteExpense(expense.id);
      onRefresh();
      onClose();
    } catch {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleDocFiles = (list: FileList | null) => {
    if (!list) return;
    const arr = Array.from(list);
    const big = arr.find((f) => f.size > MAX_FILE_BYTES);
    if (big) { setDocFileError(`"${big.name}" 2 MB sınırını aşıyor.`); return; }
    setDocFileError(null);
    setDocFiles((prev) => [...prev, ...arr]);
  };

  const handleUploadDocs = async () => {
    if (!docFiles.length) return;
    setUploadingDoc(true);
    try {
      await addExpenseDocuments(expense.id, docFiles);
      setDocFiles([]);
      onRefresh();
    } catch {
      setDocFileError("Yükleme başarısız.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    setDeletingDocId(docId);
    try {
      await deleteExpenseDocument(docId);
      onRefresh();
    } finally {
      setDeletingDocId(null);
    }
  };

  const openPreview = (url: string) => {
    setPdfNumPages(0);
    setPreviewUrl(url);
  };

  const closePreview = () => setPreviewUrl(null);

  const panelStyle: React.CSSProperties = isNarrow
    ? { position: "fixed", inset: 0, zIndex: 2040, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }
    : { position: "fixed", top: "50%", left: "50%", zIndex: 2040, transform: "translate(-50%,-50%)", width: "min(600px,calc(100vw - 24px))", maxHeight: "90dvh", background: "#fff", borderRadius: 20, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", overflow: "hidden" };

  const inp: React.CSSProperties = { width: "100%", borderRadius: 10, border: "1.5px solid #e0e7ff", padding: "9px 13px", fontSize: "0.88rem", color: "#111827", outline: "none", background: "#f8faff", fontFamily: "inherit", boxSizing: "border-box" };
  const readonlyStyle: React.CSSProperties = { ...inp, background: "#f1f5f9", color: "#6b7280", cursor: "default" };

  const curr = currencies.find((c) => c.id === form.currencyId) ?? { currencyDisplayText: expense.currencyDisplayText };

  return (
    <>
      {!isNarrow && <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 2039, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }} />}

      <div style={panelStyle}>
        {/* Header */}
        <div style={{ padding: isNarrow ? "14px 16px" : "16px 20px", background: "linear-gradient(135deg,#0f766e,#0d9488)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-receipt text-white" style={{ fontSize: "1rem" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: "0.96rem", color: "#fff" }}>Fatura Detayı</div>
            <div style={{ fontSize: "0.66rem", color: "rgba(255,255,255,0.65)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{expense.description}</div>
          </div>
          {!editing && (
            <button onClick={() => setEditing(true)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, padding: "6px 12px", color: "#fff", cursor: "pointer", fontSize: "0.78rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
              <i className="bi bi-pencil" /> Düzenle
            </button>
          )}
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "0.9rem", flexShrink: 0 }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>
        {showVendorPicker && <VendorPicker onSelect={(v) => { setSelectedVendor(v); setShowVendorPicker(false); }} onClose={() => setShowVendorPicker(false)} />}
        <div style={{ padding: isNarrow ? "14px" : "20px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Özet kartları */}
          {!editing && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <div style={{ flex: "1 1 120px", background: "linear-gradient(135deg,#ecfdf5,#d1fae5)", border: "1.5px solid #a7f3d0", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ fontSize: "0.6rem", color: "#065f46", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Tutar</div>
                <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#065f46", marginTop: 2 }}>
                  {expense.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {expense.currencyDisplayText}
                </div>
              </div>
              <div style={{ flex: "1 1 120px", background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ fontSize: "0.6rem", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Harcayan</div>
                <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#111827", marginTop: 2 }}>{expense.spentByUserName}</div>
              </div>
              <div style={{ flex: "1 1 140px", background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ fontSize: "0.6rem", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Tarih</div>
                <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#111827", marginTop: 2 }}>{formatDt(expense.spentAt)}</div>
              </div>
              <div style={{ flex: "1 1 120px", background: isReimbursed ? "#ecfdf5" : "#fef2f2", border: `1.5px solid ${isReimbursed ? "#a7f3d0" : "#fecaca"}`, borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: "0.6rem", color: isReimbursed ? "#065f46" : "#991b1b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Geri Ödeme</div>
                <div style={{ fontSize: "0.82rem", fontWeight: 700, color: isReimbursed ? "#065f46" : "#dc2626" }}>
                  {isReimbursed ? "Ödendi" : "Bekliyor"}
                </div>
                <button onClick={handleToggleReimbursed} disabled={reimbursing}
                  style={{ marginTop: 2, padding: "4px 10px", borderRadius: 7, border: "none", fontWeight: 700, fontSize: "0.7rem", cursor: reimbursing ? "default" : "pointer", background: isReimbursed ? "#fef2f2" : "#ecfdf5", color: isReimbursed ? "#dc2626" : "#065f46", display: "flex", alignItems: "center", gap: 4, alignSelf: "flex-start" }}>
                  {reimbursing
                    ? <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
                    : <i className={`bi ${isReimbursed ? "bi-arrow-counterclockwise" : "bi-check2"}`} />}
                  {isReimbursed ? "Geri Al" : "Ödendi İşaretle"}
                </button>
              </div>
            </div>
          )}

          {/* Detay / Düzenleme alanı */}
          {!editing ? (
            <div style={{ background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 12, padding: "14px" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Açıklama</div>
              <div style={{ fontSize: "0.9rem", color: "#111827", lineHeight: 1.5 }}>{expense.description}</div>
              {expense.vendorName && (
                <div style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 20, padding: "3px 10px", fontSize: "0.78rem", fontWeight: 600, color: "#b45309" }}>
                  <i className="bi bi-building" />{expense.vendorName}
                </div>
              )}
              <div style={{ marginTop: 10, fontSize: "0.72rem", color: "#9ca3af", display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span>Oluşturan: {expense.createdByUserName}</span>
                <span>·</span>
                <span>Kayıt: {formatDt(expense.createdAt)}</span>
              </div>
            </div>
          ) : (
            <div style={{ background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 14, padding: "14px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#0f766e", display: "flex", justifyContent: "space-between" }}>
                <span><i className="bi bi-pencil me-2" />Düzenleme Modu</span>
                <button onClick={() => setEditing(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1rem" }}><i className="bi bi-x" /></button>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 2 }}>
                  <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Tutar *</label>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: Number(e.target.value) }))} style={inp} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Para Birimi</label>
                  {currLoading
                    ? <div style={readonlyStyle}>Yükleniyor…</div>
                    : <select value={form.currencyId} onChange={(e) => setForm((p) => ({ ...p, currencyId: e.target.value }))} style={{ ...inp, cursor: "pointer" }}>
                        {currencies.map((c) => <option key={c.id} value={c.id}>{c.currencyDisplayText}</option>)}
                      </select>
                  }
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Harcama Tarihi</label>
                <input type="datetime-local" value={spentAtLocal} onChange={(e) => setSpentAtLocal(e.target.value)} style={inp} />
              </div>

              <div>
                <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Açıklama *</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} style={{ ...inp, resize: "vertical" }} />
              </div>

              <div>
                <label style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Firma (opsiyonel)</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 160px", ...inp, display: "flex", alignItems: "center", gap: 8, color: selectedVendor ? "#111827" : "#9ca3af", cursor: "default" }}>
                    {selectedVendor
                      ? <><i className="bi bi-building-fill" style={{ color: "#b45309" }} />{selectedVendor.name}</>
                      : <><i className="bi bi-building" />Seçilmedi</>
                    }
                    {selectedVendor && (
                      <button type="button" onClick={() => setSelectedVendor(null)} title="Kaldır"
                        style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex" }}>
                        <i className="bi bi-x-circle-fill" />
                      </button>
                    )}
                  </div>
                  <button type="button" onClick={() => setShowVendorPicker(true)}
                    style={{ flex: "0 0 auto", background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "9px 12px", fontWeight: 700, fontSize: "0.78rem", color: "#374151", cursor: "pointer", whiteSpace: "nowrap" }}>
                    <i className="bi bi-search me-1" />{selectedVendor ? "Değiştir" : "Listeden Seç"}
                  </button>
                </div>
              </div>

              {editError && <div style={{ padding: "8px 12px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: "0.78rem" }}>{editError}</div>}

              <button onClick={handleSave} disabled={saving}
                style={{ background: "linear-gradient(135deg,#0f766e,#0d9488)", border: "none", borderRadius: 10, padding: "10px 0", fontWeight: 700, fontSize: "0.88rem", color: "#fff", cursor: saving ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, boxShadow: "0 3px 12px rgba(15,118,110,0.3)" }}>
                {saving ? <><span className="spinner-border spinner-border-sm" /> Kaydediliyor…</> : <><i className="bi bi-check-lg" /> Güncelle</>}
              </button>
            </div>
          )}

          {/* Belgeler */}
          <div>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
              <i className="bi bi-paperclip me-2" style={{ color: "#6366f1" }} />
              Belgeler ({expense.documents.length})
            </div>

            {expense.documents.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                {expense.documents.map((doc) => {
                  const name = doc.documentUrl.split("/").pop() ?? "Belge";
                  const urlPath = doc.documentUrl.split("?")[0];
                  const isImg = /\.(png|jpg|jpeg|gif|webp)$/i.test(urlPath);
                  const isPdf = /\.pdf$/i.test(urlPath);
                  const canPreview = isImg || isPdf;
                  return (
                    <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "8px 12px" }}>
                      <i className={`bi ${isImg ? "bi-image" : isPdf ? "bi-file-earmark-pdf" : "bi-file-earmark"}`}
                        style={{ color: isImg ? "#10b981" : isPdf ? "#ef4444" : "#6366f1", fontSize: "1rem", flexShrink: 0 }} />
                      <span
                        onClick={() => canPreview && openPreview(doc.documentUrl)}
                        style={{ flex: 1, fontSize: "0.8rem", color: canPreview ? "#1d4ed8" : "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: canPreview ? "pointer" : "default", textDecoration: canPreview ? "underline" : "none", textUnderlineOffset: 2 }}>
                        {name}
                      </span>
                      {canPreview && (
                        <button onClick={() => openPreview(doc.documentUrl)} title="Önizle"
                          style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1d4ed8", flexShrink: 0 }}>
                          <i className="bi bi-eye" style={{ fontSize: "0.75rem" }} />
                        </button>
                      )}
                      <a href={doc.documentUrl} download={urlPath.split("/").pop()} target="_blank" rel="noopener noreferrer" title="İndir"
                        style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 7, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: "#15803d", flexShrink: 0 }}>
                        <i className="bi bi-download" style={{ fontSize: "0.75rem" }} />
                      </a>
                      <button onClick={() => handleDeleteDoc(doc.id)} disabled={deletingDocId === doc.id}
                        style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 7, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#dc2626", flexShrink: 0 }}>
                        {deletingDocId === doc.id ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <i className="bi bi-trash3" style={{ fontSize: "0.75rem" }} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Belge ekle */}
            <label style={{ display: "flex", alignItems: "center", gap: 8, border: "1.5px dashed #c7d2fe", borderRadius: 10, padding: "9px 14px", cursor: "pointer", background: "#f8faff" }}>
              <i className="bi bi-cloud-upload" style={{ color: "#6366f1" }} />
              <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Belge ekle (maks 2 MB)</span>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" style={{ display: "none" }} onChange={(e) => handleDocFiles(e.target.files)} />
            </label>
            {docFileError && <div style={{ marginTop: 5, fontSize: "0.74rem", color: "#dc2626" }}>{docFileError}</div>}
            {docFiles.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                {docFiles.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f1f5f9", borderRadius: 8, padding: "5px 10px", fontSize: "0.76rem" }}>
                    <i className="bi bi-file-earmark" style={{ color: "#6366f1" }} />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <button onClick={() => setDocFiles((p) => p.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 0 }}>
                      <i className="bi bi-x" />
                    </button>
                  </div>
                ))}
                <button onClick={handleUploadDocs} disabled={uploadingDoc}
                  style={{ background: "linear-gradient(135deg,#6366f1,#4f46e5)", border: "none", borderRadius: 9, padding: "8px 0", fontWeight: 700, fontSize: "0.82rem", color: "#fff", cursor: uploadingDoc ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 4 }}>
                  {uploadingDoc ? <><span className="spinner-border spinner-border-sm" /> Yükleniyor…</> : <><i className="bi bi-cloud-upload" /> Yükle</>}
                </button>
              </div>
            )}
          </div>

          {/* Sil */}
          {!editing && (
            <div style={{ borderTop: "1.5px solid #f1f5f9", paddingTop: 12 }}>
              {!confirmDelete ? (
                <button onClick={() => setConfirmDelete(true)}
                  style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "9px 16px", fontWeight: 700, fontSize: "0.82rem", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="bi bi-trash3" /> Faturayı Sil
                </button>
              ) : (
                <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: "0.84rem", fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>Bu faturayı silmek istediğinize emin misiniz?</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={handleDelete} disabled={deleting}
                      style={{ flex: 1, background: "#dc2626", border: "none", borderRadius: 9, padding: "8px 0", fontWeight: 700, fontSize: "0.84rem", color: "#fff", cursor: deleting ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      {deleting ? <span className="spinner-border spinner-border-sm" /> : "Evet, Sil"}
                    </button>
                    <button onClick={() => setConfirmDelete(false)}
                      style={{ flex: 1, background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "8px 0", fontWeight: 700, fontSize: "0.84rem", color: "#374151", cursor: "pointer" }}>
                      Vazgeç
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </div>

        {/* Footer */}
        <div style={{ padding: isNarrow ? "12px 16px" : "12px 20px", borderTop: "1.5px solid #f1f5f9", background: "#fafafa", flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ width: "100%", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "10px 0", fontWeight: 700, fontSize: "0.86rem", color: "#374151", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <i className="bi bi-x-lg" /> Kapat
          </button>
        </div>
      </div>

      {/* Dosya Önizleme Lightbox */}
      {previewUrl && (() => {
        const urlPath = previewUrl.split("?")[0];
        const isImg = /\.(png|jpg|jpeg|gif|webp)$/i.test(urlPath);
        const isPdf = /\.pdf$/i.test(urlPath);
        return (
          <div onClick={closePreview}
            style={{ position: "fixed", inset: 0, zIndex: 2060, background: "rgba(0,0,0,0.88)", backdropFilter: "blur(5px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>

            {/* Toolbar */}
            <div onClick={(e) => e.stopPropagation()}
              style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, width: "min(88vw,900px)" }}>
              <span style={{ flex: 1, fontSize: "0.78rem", color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {urlPath.split("/").pop()}
              </span>
              <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, color: "#fff", fontSize: "0.75rem", fontWeight: 600, textDecoration: "none" }}>
                <i className="bi bi-box-arrow-up-right" /> Yeni sekmede aç
              </a>
              <button onClick={closePreview}
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
                <i className="bi bi-x-lg" style={{ fontSize: "0.85rem" }} />
              </button>
            </div>

            {/* İçerik */}
            <div onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "88vw", maxHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>

              {isImg ? (
                <img src={previewUrl} alt="Önizleme"
                  style={{ maxWidth: "88vw", maxHeight: "80vh", borderRadius: 14, boxShadow: "0 12px 48px rgba(0,0,0,0.5)", objectFit: "contain" }} />

              ) : isPdf ? (
                <div style={{ width: "min(82vw,860px)", maxHeight: "78vh", overflowY: "auto", borderRadius: 14, background: "#f8faff", boxShadow: "0 12px 48px rgba(0,0,0,0.4)", padding: "12px 0" }}>
                  <Document
                    file={previewUrl}
                    onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                    loading={
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 48 }}>
                        <span className="spinner-border text-secondary" />
                      </div>
                    }
                    error={
                      <div style={{ padding: 32, textAlign: "center" }}>
                        <i className="bi bi-exclamation-circle" style={{ fontSize: "2rem", color: "#f59e0b" }} />
                        <div style={{ marginTop: 12, fontSize: "0.88rem", color: "#374151", fontWeight: 600 }}>PDF görüntülenemiyor</div>
                        <div style={{ marginTop: 6, fontSize: "0.76rem", color: "#6b7280" }}>S3 CORS ayarı gerekiyor. Dosyayı aşağıdan açabilirsiniz.</div>
                        <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                          style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#6366f1", color: "#fff", borderRadius: 9, fontWeight: 700, fontSize: "0.83rem", textDecoration: "none" }}>
                          <i className="bi bi-box-arrow-up-right" /> Yeni sekmede aç
                        </a>
                      </div>
                    }
                  >
                    {Array.from({ length: pdfNumPages }, (_, i) => (
                      <div key={i} style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}>
                        <Page pageNumber={i + 1} width={Math.min(window.innerWidth * 0.75, 820)}
                          renderTextLayer={false} renderAnnotationLayer={false} />
                      </div>
                    ))}
                  </Document>
                </div>
              ) : null}
            </div>
          </div>
        );
      })()}
    </>
  );
}
